import { Box, Button } from "@mui/material";
import React, { SetStateAction, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { ReactourStep } from "reactour";
import { ROUTES } from "router/types";
import I18nKey from "translations/I18nKey";
import { useEffect } from "react";
import { TourSteps } from "types";

const screenWidth = window.screen.width;

interface TourSignUp {
  readonly showSignUpModal: boolean;
  readonly openProductsModal: boolean;
  readonly isTourOpen: boolean;
  readonly scrollTo: string;
  readonly setScrollTo: React.Dispatch<SetStateAction<string>>;
  readonly isMobile: boolean;
  readonly tourSteps: ReactourStep[];
  readonly currentStep: number;
  readonly focusLock: boolean;
  readonly handleOpenSignUpModal: (open: boolean) => void;
  readonly setTourSteps: React.Dispatch<SetStateAction<ReactourStep[]>>;
  readonly setOpenProductsModal: React.Dispatch<SetStateAction<boolean>>;
  readonly handleTourStepsChange: (steps: TourSteps) => void;
  readonly handleTourOpen: (step: number) => void;
  readonly handleTourClose: () => void;
  readonly handleCloseProductsModal: () => void;
}

interface TourProviderProps {
  readonly children: JSX.Element[] | JSX.Element;
}

export const TourContext = React.createContext<TourSignUp>({
  showSignUpModal: false,
  openProductsModal: false,
  isTourOpen: false,
  scrollTo: "",
  setScrollTo: () => {},
  tourSteps: [],
  isMobile: false,
  currentStep: 0,
  focusLock: false,
  handleOpenSignUpModal: () => {},
  setTourSteps: () => {},
  setOpenProductsModal: () => {},
  handleTourStepsChange: (steps: TourSteps) => {},
  handleCloseProductsModal: () => {},
  handleTourOpen: (step: number) => {},
  handleTourClose: () => {},
});

export const TourContextProvider: React.FC<TourProviderProps> = ({
  children,
}) => {
  const intl = useIntl();
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [openProductsModal, setOpenProductsModal] = useState<boolean>(false);
  const [tourSteps, setTourSteps] = useState<ReactourStep[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
  const [scrollTo, setScrollTo] = useState<string>("");
  const [focusLock, setFocusLock] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    // You can adjust the threshold as needed

    const isMobileDevice = screenWidth <= 768;
    setIsMobile(isMobileDevice);
  }, [screenWidth]);
  const navigate = useNavigate();

  const stepStyles = {
    style: {
      height: "130px",
    },
  };
  const catalogHealthSteps = [
    {
      selector: '[data-tut="reactour__first"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_LISTING_HEALTH,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Gemini);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
    {
      selector: '[data-tut="reactour__eighth"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_CATALOG_TRACKER,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Perseverance);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
  ];
  const supplyChainSteps = [
    {
      selector: '[data-tut="reactour__second"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_REPLENISHMENT,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Kepler);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
    {
      selector: '[data-tut="reactour__fourth"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_SHIPMENTS,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Apollo);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
    {
      selector: '[data-tut="reactour__fifth"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_FORCASTING,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Magellan);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },

    {
      selector: '[data-tut="reactour__ninth"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_FULFILMENT_FEES,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Ranger);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
  ];
  const businessGrowthSteps = [
    {
      selector: '[data-tut="reactour__third"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_SALES,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.SalesChanges);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
    {
      selector: '[data-tut="reactour__sixth"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_PERFORMANCE_DASHBOARD,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Rosetta);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
    {
      selector: '[data-tut="reactour__seventh"]',
      content: () => (
        <>
          {intl.formatMessage({
            id: I18nKey.TOUR_PAYMENTS,
          })}
          <Box sx={{ position: "absolute", bottom: "10px" }}>
            <Button
              sx={{ textTransform: "none !important" }}
              onClick={() => {
                navigate(ROUTES.Pheonix);
                setIsTourOpen(false);
              }}
            >
              {intl.formatMessage({
                id: I18nKey.VIEW_DETAILS_BTN,
              })}
            </Button>
            <br />
          </Box>
        </>
      ),
      ...stepStyles,
    },
  ];
  const sellerAccountSteps: ReactourStep[] = [
    {
      selector: '[data-tut="reactour__signUp"]',
      content: `${intl.formatMessage({
        id: I18nKey.TOUR_SIGNUP,
      })}`,
      position: "left",
      style: {
        top: "-140px",
        left: "430px",
      },
    },
  ];
  const steps = {
    catalogHealthSteps: catalogHealthSteps,
    supplyChainSteps: supplyChainSteps,
    businessGrowthSteps: businessGrowthSteps,
    sellerAccountSteps: sellerAccountSteps,
  };

  const handleOpenSignUpModal = (open: boolean) => {
    setShowSignUpModal(open);
    if (!open) if (isTourOpen) handleTourClose();
  };
  const handleTourStepsChange = (step: TourSteps) => {
    setTourSteps(steps[step]);
    if (step === TourSteps.SELLER_ACCOUNT) setFocusLock(true);
  };

  const handleTourOpen = (step: number) => {
    setCurrentStep(step);
    setIsTourOpen(true);
  };

  const handleTourClose = () => {
    setCurrentStep(0);
    setIsTourOpen(false);
  };

  const handleCloseProductsModal = () => {
    setOpenProductsModal(false);
  };

  return (
    <TourContext.Provider
      value={{
        showSignUpModal,
        openProductsModal,
        isTourOpen,
        scrollTo,
        setScrollTo,
        isMobile,
        tourSteps,
        currentStep,
        focusLock,
        setTourSteps,
        setOpenProductsModal,
        handleOpenSignUpModal,
        handleTourStepsChange,
        handleCloseProductsModal,
        handleTourClose,
        handleTourOpen,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
